import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Button,
  Form,
  Table,
  Pagination,
  InputGroup,
  Toast,
  Nav,
  Badge,
  ListGroup,
  Spinner,
  Alert,
  Modal
} from "react-bootstrap";
import supabase from "../services/supabase";
import _ from 'lodash';
import EntityLogoManager from './UniversityLogoManager';
import { v4 as uuidv4 } from 'uuid';

const API_URL = "https://dashport-admin-api.lookermetrics.com";

export const AdminInterface = () => {
  const calculateTrialEnd = (duration) => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + parseInt(duration));
    return endDate.toISOString().split('T')[0];
  };

  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: "",
    // Metadata fields
    firstName: "",
    lastName: "",
    organization: "",
    role: "",
    // Existing fields
    university_token: "",
    university_name: "",
    variant: "Essentials",
    accountType: "trial",
    trialDuration: "14",
    trialEndsAt: calculateTrialEnd("14"),
    custom_links: [{ name: "", url: "" }],
  });
  const [originalTrialEndsAt, setOriginalTrialEndsAt] = useState(null);
  const [originalTrialDuration, setOriginalTrialDuration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPasswordToast, setShowPasswordToast] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const toastRef = useRef(null);
  const [activeTab, setActiveTab] = useState("users");
  const [universities, setUniversities] = useState([]);
  const [newUniversity, setNewUniversity] = useState({
    name: "",
    scholarUrl: "",
    yearsBack: "5",
    isTrial: true
  });
  const [inviteUser, setInviteUser] = useState(true);
  
  // New states for OpenAlex institution search
  const [universitySearchTerm, setUniversitySearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Update state variables for BigQuery processing
  const [isProcessing, setIsProcessing] = useState(false);
  const [bigQueryStatus, setBigQueryStatus] = useState('');
  
  // New states for OpenAlex funder search
  const [funderSearchTerm, setFunderSearchTerm] = useState('');
  const [funderSearchResults, setFunderSearchResults] = useState([]);
  const [selectedFunder, setSelectedFunder] = useState(null);
  const [isFunderSearching, setIsFunderSearching] = useState(false);

  const [passwordResetStatus, setPasswordResetStatus] = useState({ email: null, status: null, message: "" });
  const [passwordResetModal, setPasswordResetModal] = useState({ show: false, email: null });

  const calculateTrialDuration = (endDate) => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);  // Set to start of day
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);  // Set to start of day
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateExtendedDuration = (newEndDate) => {
    if (!originalTrialEndsAt || !originalTrialDuration) return null;
    
    const originalEnd = new Date(originalTrialEndsAt);
    const newEnd = new Date(newEndDate);
    const diffDays = Math.ceil((newEnd - originalEnd) / (1000 * 60 * 60 * 24));
    
    return parseInt(originalTrialDuration) + diffDays;
  };

  const fetchUsers = useCallback(async () => {
    const { data, error } = await supabase
      .from("iptalons_parameters")
      .select("*");
    if (error) {
      console.error("Error fetching users:", error);
      setError("Failed to fetch users");
    } else {
      setUsers(
        data.map((user) => ({
          ...user,
          university_token: user.parameter,
          accountType: user.account_type || 'paid',
          trialEndsAt: user.trial_ends_at || '',
          trialDuration: user.trial_duration || '',
          custom_links: Array.isArray(user.custom_links)
            ? user.custom_links
            : JSON.parse(user.custom_links || "[]"),
        }))
      );
    }
  }, []);

  const fetchUniversities = useCallback(async () => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) throw new Error("No access token found");

      const response = await fetch('https://dashport-integrations-api.lookermetrics.com/extractions', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) throw new Error("Failed to fetch universities");

      const data = await response.json();
      setUniversities(data);
    } catch (error) {
      console.error("Error fetching universities:", error);
      setError("Failed to fetch universities");
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchUniversities();
  }, [fetchUsers, fetchUniversities]);

  const getAccessToken = () => {
    const tokenKey = Object.keys(localStorage).find((key) =>
      key.endsWith("-auth-token")
    );
    if (tokenKey) {
      const tokenData = JSON.parse(localStorage.getItem(tokenKey));
      return tokenData.access_token;
    }
    return null;
  };

  // New function to handle university search via OpenAlex API
  const handleUniversitySearch = (e) => {
    setUniversitySearchTerm(e.target.value);
    
    // Auto-search after typing
    if (e.target.value.length > 2) {
      debouncedSearch(e.target.value);
    } else {
      setSearchResults([]);
    }
  };

  // Define searchUniversities inside a useCallback to avoid dependency changes
  const searchUniversities = useCallback(async (term) => {
    if (!term || term.length < 3) return;
    
    setIsSearching(true);
    try {
      const response = await fetch(`https://api.openalex.org/autocomplete/institutions?q=${encodeURIComponent(term)}`);
      
      // If we hit rate limit (429), just silently wait and return
      if (response.status === 429) {
        console.log('Rate limit reached for OpenAlex API');
        return;
      }
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (error) {
      console.error('Error searching universities:', error);
      // Don't show any errors to the user
    } finally {
      setIsSearching(false);
    }
  }, []);
  
  // Debounce search to avoid too many API calls
  const debouncedSearch = useCallback(
    (term) => {
      const debounced = _.debounce((searchTerm) => {
        searchUniversities(searchTerm);
      }, 500); // Increased debounce time to reduce API calls
      debounced(term);
      return () => debounced.cancel();
    },
    [searchUniversities]
  );

  const selectUniversity = (university) => {
    setSelectedUniversity(university);
    setSearchResults([]);
    setUniversitySearchTerm('');
    
    // Update the newUser state with the selected university info
    if (newUser.variant === "RedBook Super Admin") {
      const dashboardUrl = constructDashboardUrl(university.id, 'institution');
      setNewUser({
        ...newUser,
        university_token: dashboardUrl,
        university_name: university.display_name,
        custom_links: newUser.custom_links
      });
    } else if (newUser.variant === "RedBook Medical Super Admin") {
      const dashboardUrl = constructDashboardUrl(university.id, 'medical');
      setNewUser({
        ...newUser,
        university_token: dashboardUrl,
        university_name: university.display_name,
        custom_links: newUser.custom_links
      });
    } else if (newUser.variant === "RedBook for Funders Super Admin") {
      const dashboardUrl = constructDashboardUrl(university.id, 'funder');
      setNewUser({
        ...newUser,
        university_token: dashboardUrl,
        university_name: university.display_name,
        custom_links: newUser.custom_links
      });
    } else {
      setNewUser({
        ...newUser,
        university_token: university.id,
        university_name: university.display_name,
        custom_links: newUser.custom_links
      });
    }
  };

  const constructDashboardUrl = (id, type = 'institution') => {
    if (type === 'institution') {
      // Base URL for the RedBook Super Admin dashboard
      const baseUrl = 'https://lookerstudio.google.com/embed/reporting/4a41b1ed-b5e5-4fa6-9df4-67b566457133/page/Hpd7D';
      
      const params = {
        "ds255.primary_institution": id,
        "ds254.primary_institution": id
      };
      
      const paramsEncoded = encodeURIComponent(JSON.stringify(params));
      return `${baseUrl}?params=${paramsEncoded}`;
    } else if (type === 'medical') {
      // Base URL for the RedBook Medical Super Admin dashboard
      const baseUrl = 'https://lookerstudio.google.com/embed/reporting/3a17f768-2081-4760-bb45-f23997c282bd/page/Hpd7D';
      
      const params = {
        "ds255.primary_institution": id,
        "ds254.primary_institution": id
      };
      
      const paramsEncoded = encodeURIComponent(JSON.stringify(params));
      return `${baseUrl}?params=${paramsEncoded}`;
    } else if (type === 'funder') {
      // Base URL for the RedBook for Funders Super Admin dashboard
      const baseUrl = 'https://lookerstudio.google.com/embed/reporting/2456e1ad-7c04-4791-a150-a620f369be46/page/Hpd7D';
      
      const params = {
        "ds254.funderid": id
      };
      
      const paramsEncoded = encodeURIComponent(JSON.stringify(params));
      return `${baseUrl}?params=${paramsEncoded}`;
    }
  };

  const extractOpenAlexId = (url) => {
    try {
      // If it's already an OpenAlex URL, return it
      if (url.includes('openalex.org/I')) {
        return url;
      }
      
      // If it's a dashboard URL, extract the OpenAlex URL from the params
      const paramsMatch = url.match(/params=(.+)$/);
      if (paramsMatch) {
        const params = JSON.parse(decodeURIComponent(paramsMatch[1]));
        const openAlexUrl = params["ds255.primary_institution"];
        // If we found an OpenAlex URL in the params, return it
        if (openAlexUrl && openAlexUrl.includes('openalex.org/I')) {
          return openAlexUrl;
        }
      }
      
      // If no match found or not an OpenAlex URL, try to extract just the ID
      const idMatch = url.match(/openalex\.org\/I(\d+)/);
      if (idMatch) {
        return `https://openalex.org/I${idMatch[1]}`;
      }
      
      // If all else fails, return the original URL
      return url;
    } catch (e) {
      // If there's any error parsing, return the original URL
      return url;
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  // Sort users to always show admin@iptalons.com and funders.redbook@iptalons.com first
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (a.email === "admin@iptalons.com") return -1;
    if (b.email === "admin@iptalons.com") return 1;
    if (a.email === "funders.redbook@iptalons.com") return -1;
    if (b.email === "funders.redbook@iptalons.com") return 1;
    return 0;
  });
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleInputChange = (e, index, field) => {
    if (field === "trialDuration") {
      const duration = e.target.value;
      setNewUser({
        ...newUser,
        trialDuration: duration,
        trialEndsAt: calculateTrialEnd(duration)
      });
    } else if (field === "trialEndsAt") {
      const newEndDate = e.target.value;
      if (editingUser) {
        // For editing: calculate extended duration
        const extendedDuration = calculateExtendedDuration(newEndDate);
        setNewUser({
          ...newUser,
          trialEndsAt: newEndDate,
          trialDuration: extendedDuration?.toString() || newUser.trialDuration
        });
      } else {
        // For new user: calculate duration from selected date
        setNewUser({
          ...newUser,
          trialEndsAt: newEndDate,
          trialDuration: calculateTrialDuration(newEndDate).toString()
        });
      }
    } else if (field === "university") {
      const selectedUniversity = universities.find(uni => uni.university_token === e.target.value);
      
      // If variant is Redbook, update the Researcher Network URL
      if (newUser.variant === "Redbook") {
        const researcherNetworkUrl = selectedUniversity 
          ? `https://release-feb6-2025.researcher-network-viz.pages.dev/?token=${selectedUniversity.university_token}`
          : "";
        
        setNewUser({ 
          ...newUser, 
          university_token: e.target.value,
          university_name: selectedUniversity ? selectedUniversity.university_name : "",
          custom_links: [
            { name: "Risk Viewer", url: researcherNetworkUrl },
            { name: "Investigator", url: "https://risk-mitigation-plan.pages.dev"}
          ]
        });
      } else {
        // Normal university selection handling for other variants
        setNewUser({ 
          ...newUser, 
          university_token: e.target.value,
          university_name: selectedUniversity ? selectedUniversity.university_name : ""
        });
      }
    } else if (field === "custom_links") {
      const updatedLinks = [...newUser.custom_links];
      updatedLinks[index] = {
        ...updatedLinks[index],
        [e.target.name]: e.target.value,
      };
      setNewUser({ ...newUser, custom_links: updatedLinks });
    } else if (e.target.name === "variant") {
      if (e.target.value === "Natcast" && newUser.variant !== "Natcast") {
        setNewUser(prev => ({
          ...prev,
          variant: e.target.value,
          custom_links: [
            { name: "Grant Hopper", url: "https://granthopper.iptalons.com" },
            { name: "Risk Viewer", url: "https://riskviewer.iptalons.com/?token=b0dd820a-e79d-439b-9d5e-6be173695444" },
            { name: "Inventor Watch", url: "https://lookerstudio.google.com/embed/reporting/b39f1475-c175-4137-8abd-47e9678399ed/page/p_k2txyepgld" }
          ]
        }));
      } else if (e.target.value === "RedBook Super Admin") {
        // When switching to Super Admin, preserve existing custom links
        // and update the university_token with the dashboard URL if a university is selected
        if (selectedUniversity) {
          const dashboardUrl = constructDashboardUrl(selectedUniversity.id);
          setNewUser(prev => ({
            ...prev,
            variant: e.target.value,
            university_token: dashboardUrl,
            custom_links: prev.custom_links // Preserve existing custom links
          }));
        } else {
          setNewUser(prev => ({
            ...prev,
            variant: e.target.value,
            custom_links: prev.custom_links // Preserve existing custom links
          }));
        }
      } else if (e.target.value === "RedBook Medical Super Admin") {
        // When switching to Medical Super Admin, preserve existing custom links
        // and update the university_token with the dashboard URL if a university is selected
        if (selectedUniversity) {
          const dashboardUrl = constructDashboardUrl(selectedUniversity.id, 'medical');
          setNewUser(prev => ({
            ...prev,
            variant: e.target.value,
            university_token: dashboardUrl,
            custom_links: prev.custom_links // Preserve existing custom links
          }));
        } else {
          setNewUser(prev => ({
            ...prev,
            variant: e.target.value,
            custom_links: prev.custom_links // Preserve existing custom links
          }));
        }
      } else {
        // For other variants, just update the variant and preserve custom links
        setNewUser(prev => ({
          ...prev,
          variant: e.target.value,
          custom_links: prev.custom_links // Preserve existing custom links
        }));
      }
      return;
    } else {
      setNewUser({ ...newUser, [e.target.name]: e.target.value });
    }
  };

  const addField = (field) => {
    if (field === "custom_links") {
      setNewUser({
        ...newUser,
        custom_links: [...newUser.custom_links, { name: "", url: "" }],
      });
    }
  };

  const removeField = (index, field) => {
    if (field === "custom_links") {
      const updatedLinks = newUser.custom_links.filter((_, i) => i !== index);
      setNewUser({ ...newUser, custom_links: updatedLinks });
    }
  };

  const generatePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const password = Array.from(
      { length: 15 },
      () => chars[Math.floor(Math.random() * chars.length)]
    ).join("");
    setGeneratedPassword(password);
    return password;
  };

  const validateForm = () => {
    // Basic required fields except university and role
    if (!newUser.email || !newUser.firstName || !newUser.lastName || !newUser.organization) {
      setError("Please fill in all required fields.");
      return false;
    }

    // Role is only required for Natcast variant
    if (newUser.variant === "Natcast" && !newUser.role) {
      setError("Please select a role for Natcast user.");
      return false;
    }

    // University selection validation
    if (newUser.variant === "RedBook Super Admin") {
      if (!selectedUniversity && !newUser.university_token) {
        setError("Please search and select a university for RedBook Super Admin.");
        return false;
      }
    } else if (newUser.variant === "RedBook Medical Super Admin") {
      if (!selectedUniversity && !newUser.university_token) {
        setError("Please search and select a university for RedBook Medical Super Admin.");
        return false;
      }
    } else if (newUser.variant !== "Natcast" && !newUser.university_token) {
      setError("Please select a university.");
      return false;
    }

    // Validate custom links
    if (newUser.custom_links.some((link) => !link.name.trim() || !link.url.trim())) {
      setError("Please fill in both name and URL for all custom links or remove empty ones.");
      return false;
    }

    return true;
  };

  const createOrUpdateUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    let password;
    if (!inviteUser) {
      password = generatePassword();
    }

    const userDataToSave = {
      email: newUser.email,
      useInvitation: inviteUser,
      metadata: {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        organization: newUser.organization,
        role: newUser.role
      },
      ...(password && { password }), // Only include password if not using invitation
      userData: {
        university_token: newUser.university_token,
        university_name: newUser.university_name,
        variant: newUser.variant,
        account_type: newUser.accountType,
        trial_duration: newUser.trialDuration,
        trial_ends_at: newUser.trialEndsAt,
        custom_links: newUser.custom_links,
      },
    };

    try {
      const accessToken = getAccessToken();
      if (!accessToken) throw new Error("No access token found");

      const url = editingUser
        ? `${API_URL}/users/${editingUser.email}`
        : `${API_URL}/users`;
      const method = editingUser ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(userDataToSave),
      });

      if (!response.ok) throw new Error("Failed to create/update user");

      setNewUser({
        email: "",
        firstName: "",
        lastName: "",
        organization: "",
        role: "",
        university_token: "",
        university_name: "",
        variant: "Essentials",
        accountType: "trial",
        trialDuration: "14",
        trialEndsAt: "",
        custom_links: [{ name: "", url: "" }],
      });
      setEditingUser(null);
      setShowForm(false);
      fetchUsers();

      if (!editingUser && !inviteUser) {
        setShowPasswordToast(true);
      }
    } catch (error) {
      console.error("Error creating/updating user:", error);
      setError("Failed to create/update user");
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (email) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const accessToken = getAccessToken();
        if (!accessToken) throw new Error("No access token found");

        const response = await fetch(`${API_URL}/users/${email}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!response.ok) throw new Error("Failed to delete user");
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        setError("Failed to delete user");
      }
    }
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(generatedPassword).then(
      () => {},
      (err) => {
        console.error("Could not copy password: ", err);
      }
    );
  };

  const startEditing = (user) => {
    setEditingUser(user);
    setOriginalTrialEndsAt(user.trial_ends_at);
    setOriginalTrialDuration(user.trial_duration);
    
    // Split the name field into firstName and lastName
    let firstName = '', lastName = '';
    if (user.name) {
      const nameParts = user.name.split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }

    // Format the trial end date for the date picker
    let formattedTrialEndsAt = "";
    if (user.trial_ends_at) {
      const date = new Date(user.trial_ends_at);
      formattedTrialEndsAt = date.toISOString().split('T')[0];
    }
    
    // If this is a Super Admin or Funders Super Admin, try to load the entity data
    if (user.variant === "RedBook Super Admin") {
      const openAlexId = extractOpenAlexId(user.parameter);
      if (openAlexId) {
        setSelectedUniversity({
          id: openAlexId,
          display_name: user.university_name,
          hint: "" // We don't have this stored, but it's not critical
        });
      }
    } else if (user.variant === "RedBook for Funders Super Admin") {
      const funderId = extractFunderId(user.parameter);
      if (funderId) {
        setSelectedFunder({
          id: funderId,
          display_name: user.university_name
          // Not setting works_count since we don't have it
        });
      }
    } else {
      setSelectedUniversity(null);
      setSelectedFunder(null);
    }
    
    setNewUser({
      email: user.email,
      firstName: firstName,
      lastName: lastName,
      organization: user.organization || '',
      role: user.role || '',
      university_token: user.parameter,
      university_name: user.university_name,
      variant: user.variant || "Essentials",
      accountType: user.account_type || "trial",
      trialDuration: user.trial_duration || "14",
      trialEndsAt: formattedTrialEndsAt,
      custom_links: Array.isArray(user.custom_links)
        ? user.custom_links
        : JSON.parse(user.custom_links || "[]"),
    });
    setShowForm(true);
  };

  const renderUserManagement = () => (
    <>
      {!showForm ? (
        <>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <InputGroup className='w-50'>
              <InputGroup.Text>
                <i className='bi bi-search' style={{ fontSize: "18px", fontWeight: "bold" }}></i>
              </InputGroup.Text>
              <Form.Control
                type='text'
                placeholder='Search by email'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button onClick={() => setShowForm(true)}>
              <i className='bi bi-plus-circle' style={{ fontSize: "18px", fontWeight: "bold", marginRight: "6px" }}></i>
              Add New User
            </Button>
          </div>
          <div className='table-responsive'>
            <Table className='mt-4'>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Org</th>
                  <th>Role</th>
                  <th>University</th>
                  <th>Variant</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Expiration</th>
                  <th>Custom Links</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((user) => (
                  <tr key={user.email}>
                    <td>{user.email}</td>
                    <td>{(user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : '-'}</td>
                    <td>{user.organization || '-'}</td>
                    <td>{user.role || '-'}</td>
                    <td>{user.university_name || '-'}</td>
                    <td>{user.variant || 'Essentials'}</td>
                    <td>
                      <Badge bg={user.accountType === 'trial' ? 'warning' : 'success'}>
                        {user.accountType === 'trial' ? 'Trial' : 'Paid'}
                      </Badge>
                    </td>
                    <td>
                      {user.accountType === 'trial' && (
                        <Badge bg={new Date(user.trialEndsAt) > new Date() ? 'success' : 'danger'}>
                          {new Date(user.trialEndsAt) > new Date() ? 'Active' : 'Expired'}
                        </Badge>
                      )}
                    </td>
                    <td>
                      {user.accountType === 'trial' ? (
                        <div className="d-flex align-items-center">
                          {new Date(user.trialEndsAt).toLocaleDateString()}
                        </div>
                      ) : '-'}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {user.custom_links.length > 0 ? user.custom_links.length : "-"}
                    </td>
                    <td>
                      <div style={{ display: "flex", justifyContent: "start" }}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => startEditing(user)}
                          className="me-2"
                        >
                          <i className='bi bi-pencil' style={{ fontSize: "14px", fontWeight: "bold" }}></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => sendPasswordReset(user.email)}
                          className="me-2"
                          title="Send password reset email"
                        >
                          <i className='bi bi-envelope' style={{ fontSize: "14px", fontWeight: "bold" }}></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => deleteUser(user.email)}
                        >
                          <i className='bi bi-trash' style={{ fontSize: "14px", fontWeight: "bold" }}></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {Array.from({
                  length: Math.max(0, usersPerPage - currentUsers.length),
                }).map((_, index) => (
                  <tr key={`empty-${index}`} style={{ height: "48px" }}>
                    <td colSpan='11'>&nbsp;</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className='d-flex justify-content-between align-items-center mt-2'>
            <div style={{ fontSize: "small", marginLeft: "4px" }}>
              {`${users.length} / 10 users`}
            </div>
            <Pagination className='mt-3 justify-content-end'>
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => currentPage > 1 && paginate(currentPage - 1)}
              />
              {[...Array(Math.ceil(users.length / usersPerPage)).keys()].map(
                (number) => (
                  <Pagination.Item
                    key={number + 1}
                    active={number + 1 === currentPage}
                    onClick={() => paginate(number + 1)}
                  >
                    {number + 1}
                  </Pagination.Item>
                )
              )}
              <Pagination.Next
                disabled={currentPage === Math.ceil(users.length / usersPerPage)}
                onClick={() =>
                  currentPage < Math.ceil(users.length / usersPerPage) &&
                  paginate(currentPage + 1)
                }
              />
            </Pagination>
          </div>
        </>
      ) : (
        <Form onSubmit={createOrUpdateUser}>
          <div className="d-flex mb-3">
            <Form.Group className="me-2" style={{width: "50%"}}>
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                name="firstName" 
                value={newUser.firstName}
                onChange={(e) => handleInputChange(e)}
                required
              />
            </Form.Group>

            <Form.Group style={{width: "50%"}}>
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                name="lastName"
                value={newUser.lastName}
                onChange={(e) => handleInputChange(e)}
                required
              />
            </Form.Group>
          </div>

          <div className="d-flex mb-3">
            <Form.Group className="me-2" style={{width: "50%"}}>
              <Form.Label>Organization</Form.Label>
              <Form.Control
                type="text"
                name="organization"
                value={newUser.organization}
                onChange={(e) => handleInputChange(e)}
                required
              />
            </Form.Group>

            <Form.Group style={{width: "50%"}}>
              <Form.Label>Role</Form.Label>
              <Form.Select
                name="role"
                value={newUser.role}
                onChange={(e) => handleInputChange(e)}
              >
                <option value="">Select a role (only use for Natcast)</option>
                <option value="Analyst">Analyst</option>
                <option value="Admin">Admin</option>
              </Form.Select>
            </Form.Group>
          </div>

          {!editingUser && (
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Send invitation email"
                checked={inviteUser}
                onChange={(e) => setInviteUser(e.target.checked)}
              />
            </Form.Group>
          )}

          <Form.Group className='mb-3'>
            <Form.Label>Email</Form.Label>
            <Form.Control
              type='email'
              name='email'
              value={newUser.email}
              onChange={(e) => handleInputChange(e)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Account Type</Form.Label>
            <Form.Select
              name="accountType"
              value={newUser.accountType}
              onChange={(e) => handleInputChange(e)}
              required
            >
              <option value="trial">Trial</option>
              <option value="paid">Paid Subscription</option>
            </Form.Select>
          </Form.Group>

          {newUser.accountType === 'trial' && (
            <Form.Group className="mb-3">
              <Form.Label>Trial Duration</Form.Label>
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: "50%" }}>
                  <Form.Select
                    name="trialDuration"
                    value={newUser.trialDuration}
                    onChange={(e) => handleInputChange(e, null, "trialDuration")}
                    disabled={editingUser}
                  >
                    {!["7", "14", "30", "60", "90"].includes(newUser.trialDuration) && (
                      <option value={newUser.trialDuration}>{newUser.trialDuration} days</option>
                    )}
                    <option value="7">7 days</option>
                    <option value="14">14 days</option>
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                  </Form.Select>
                </div>
                <div style={{ width: "50%" }}>
                  <Form.Control
                    type="date"
                    name="trialEndsAt"
                    value={newUser.trialEndsAt}
                    onChange={(e) => handleInputChange(e, null, "trialEndsAt")}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <Form.Text className="text-muted">
                {editingUser ? (
                  <>
                    Original trial duration: {originalTrialDuration} days. 
                    New total duration will be: {newUser.trialDuration} days
                  </>
                ) : (
                  `Trial will expire on ${new Date(newUser.trialEndsAt).toLocaleDateString()}`
                )}
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className='mb-3'>
            <Form.Label>Dashboard Variant</Form.Label>
            <Form.Select
              name='variant'
              value={newUser.variant}
              onChange={(e) => {
                handleInputChange(e);
              }}
              required
            >
              <option value="Essentials">Essentials</option>
              <option value="Redbook">Redbook</option>
              <option value="RedBook Super Admin">RedBook Super Admin</option>
              <option value="RedBook Medical Super Admin">RedBook Medical Super Admin</option>
              <option value="RedBook for Funders Super Admin">RedBook for Funders Super Admin</option>
              <option value="Natcast">Natcast</option>
            </Form.Select>
          </Form.Group>

          {/* OpenAlex University Search for Super Admin */}
          {(newUser.variant === "RedBook Super Admin" || newUser.variant === "RedBook Medical Super Admin") && (
            <Form.Group className='mb-3'>
              <Form.Label>Search OpenAlex for Institution</Form.Label>
              <InputGroup>
                <Form.Control
                  type='text'
                  placeholder='Type to search for a university...'
                  value={universitySearchTerm}
                  onChange={handleUniversitySearch}
                />
                <Button 
                  variant='outline-secondary'
                  onClick={() => searchUniversities()}
                  disabled={isSearching || universitySearchTerm.length < 3}
                >
                  {isSearching ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className='bi bi-search'></i>
                  )}
                </Button>
              </InputGroup>
              
              {/* Show search results */}
              {searchResults.length > 0 && (
                <ListGroup className='mt-2'>
                  {searchResults.map(result => (
                    <ListGroup.Item 
                      key={result.id} 
                      action 
                      onClick={() => selectUniversity(result)}
                      className='d-flex justify-content-between align-items-center'
                    >
                      <div>{result.display_name}</div>
                      <small className='text-muted'>{result.hint}</small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
              
              {/* Show selected university */}
              {selectedUniversity && (
                <div className='mt-2 p-2 border rounded bg-light'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <div>
                      <strong>Selected:</strong> {selectedUniversity.display_name} 
                      {selectedUniversity.hint && <small className='ms-2 text-muted'>({selectedUniversity.hint})</small>}
                    </div>
                    <Button 
                      variant='outline-danger' 
                      size='sm'
                      onClick={() => {
                        setSelectedUniversity(null);
                        setNewUser({
                          ...newUser,
                          university_token: "",
                          university_name: "",
                        });
                      }}
                    >
                      <i className='bi bi-x'></i>
                    </Button>
                  </div>
                  <div className='mt-1'>
                    <small>OpenAlex ID: <code><a href={extractOpenAlexId(selectedUniversity.id)} target="_blank" rel="noopener noreferrer">{extractOpenAlexId(selectedUniversity.id)}</a></code></small>
                  </div>

                  {/* Update the logo manager for universities */}
                  <EntityLogoManager selectedEntity={selectedUniversity} entityType="university" />
                </div>
              )}
            </Form.Group>
          )}

          {/* Add Funder Search Section */}
          {newUser.variant === "RedBook for Funders Super Admin" && (
            <Form.Group className='mb-3'>
              <Form.Label>Search OpenAlex for Funder</Form.Label>
              <InputGroup>
                <Form.Control
                  type='text'
                  placeholder='Type to search for a funder...'
                  value={funderSearchTerm}
                  onChange={handleFunderSearch}
                />
                <Button 
                  variant='outline-secondary'
                  onClick={() => searchFunders(funderSearchTerm)}
                  disabled={isFunderSearching || funderSearchTerm.length < 3}
                >
                  {isFunderSearching ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className='bi bi-search'></i>
                  )}
                </Button>
              </InputGroup>
              
              {/* Show funder search results */}
              {funderSearchResults.length > 0 && (
                <ListGroup className='mt-2'>
                  {funderSearchResults.map(result => (
                    <ListGroup.Item 
                      key={result.id} 
                      action 
                      onClick={() => selectFunder(result)}
                      className='d-flex justify-content-between align-items-center'
                    >
                      <div>{result.display_name}</div>
                      <small className='text-muted'>{result.hint || `Works: ${result.works_count.toLocaleString()}`}</small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
              
              {/* Show selected funder */}
              {selectedFunder && (
                <div className='mt-2 p-2 border rounded bg-light'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <div>
                      <strong>Selected:</strong> {selectedFunder.display_name}
                      {selectedFunder.works_count > 0 && (
                        <small className='ms-2 text-muted'>
                          (Works: {selectedFunder.works_count.toLocaleString()})
                        </small>
                      )}
                    </div>
                    <Button 
                      variant='outline-danger' 
                      size='sm'
                      onClick={() => {
                        setSelectedFunder(null);
                        setNewUser({
                          ...newUser,
                          university_token: "",
                          university_name: "",
                        });
                      }}
                    >
                      <i className='bi bi-x'></i>
                    </Button>
                  </div>
                  <div className='mt-1'>
                    <small>OpenAlex ID: <code><a href={selectedFunder.id} target="_blank" rel="noopener noreferrer">{selectedFunder.id}</a></code></small>
                  </div>

                  {/* Add the logo manager for funders */}
                  <EntityLogoManager selectedEntity={selectedFunder} entityType="funder" />
                </div>
              )}
            </Form.Group>
          )}

          {/* Regular University Selection */}
          {newUser.variant !== "Natcast" && 
           newUser.variant !== "RedBook Super Admin" && 
           newUser.variant !== "RedBook Medical Super Admin" && 
           newUser.variant !== "RedBook for Funders Super Admin" && (
            <Form.Group className='mb-3'>
              <Form.Label>University</Form.Label>
              <Form.Select
                name='university'
                value={newUser.university_token}
                onChange={(e) => handleInputChange(e, null, 'university')}
              >
                <option value="" disabled={!!newUser.university_token}>
                  {newUser.university_token ? newUser.university_name : "Select a university"}
                </option>
                {universities.map((uni) => (
                  <option key={uni.university_token} value={uni.university_token}>
                    {uni.university_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className='mb-3'>
            <Form.Label style={{ marginRight: "8px" }}>Custom Links</Form.Label>
            {newUser.custom_links.map((link, index) => (
              <div key={index} className='d-flex mb-2'>
                <Form.Control
                  type='text'
                  name='name'
                  placeholder='Link Name'
                  value={link.name}
                  onChange={(e) => handleInputChange(e, index, "custom_links")}
                  required
                />
                <Form.Control
                  type='url'
                  name='url'
                  placeholder='URL'
                  value={link.url}
                  onChange={(e) => handleInputChange(e, index, "custom_links")}
                  required
                  className='ms-2'
                />
                <Button
                  variant='outline-danger'
                  onClick={() => removeField(index, "custom_links")}
                  className='ms-2'
                  disabled={(newUser.variant === "RedBook Super Admin" || newUser.variant === "RedBook Medical Super Admin") && index === 0}
                >
                  <i
                    className='bi bi-x'
                    style={{ fontSize: "16px", fontWeight: "bold" }}
                  ></i>
                </Button>
              </div>
            ))}
            <Button
              variant='outline-primary'
              onClick={() => addField("custom_links")}
            >
              <i
                className='bi bi-plus-circle'
                style={{ fontSize: "16px", fontWeight: "bold" }}
              ></i>{" "}
              Add Custom Link
            </Button>
          </Form.Group>
          <Button type='submit' disabled={loading}>
            {loading
              ? "Saving..."
              : editingUser
              ? "Update User"
              : "Create User"}
          </Button>
          <Button
            variant='secondary'
            onClick={() => {
              setShowForm(false);
              setEditingUser(null);
              setSelectedUniversity(null);
              setNewUser({
                email: "",
                firstName: "",
                lastName: "",
                organization: "",
                role: "",
                university_token: "",
                university_name: "",
                variant: "Essentials",
                accountType: "trial",
                trialDuration: "14",
                trialEndsAt: "",
                custom_links: [{ name: "", url: "" }],
              });
            }}
            className='ms-2'
          >
            Cancel
          </Button>
        </Form>
      )}
    </>
  );

  const extractUniversityId = (url) => {
    // Check if it's a Google Scholar URL
    const match = url.match(/org=(\d+)/);
    if (match) return match[1];
    
    // Check if it's an OpenAlex URL
    if (url.includes('openalex.org')) {
      return url;
    }
    
    return null;
  };

  const handleUniversityInputChange = (e) => {
    const { name, value } = e.target;
    setNewUniversity({ ...newUniversity, [name]: value });
  };

  const addUniversity = async (e) => {
    e.preventDefault();
    setError(null);

    const universityId = extractUniversityId(newUniversity.scholarUrl);
    if (!universityId) {
      setError("Invalid Scholar URL");
      return;
    }

    const yearsBack = newUniversity.yearsBack === "all-time" ? 0 : parseInt(newUniversity.yearsBack);

    // Generate a university token
    const universityToken = uuidv4();

    const newUniversityData = {
      university_name: newUniversity.name,
      scholar_url: newUniversity.scholarUrl,
      status: "Pending",
      researchers_count: null,
      last_sync_date: new Date().toISOString(),
      years_back: yearsBack,
      is_trial: newUniversity.isTrial,
      university_token: universityToken
    };

    // Update state to include the token
    setNewUniversity({
      ...newUniversity,
      universityToken: universityToken
    });

    setUniversities([newUniversityData, ...universities]);

    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error("No access token found");
      }

      const response = await fetch('https://dashport-integrations-api.lookermetrics.com/trigger-extraction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          universityName: newUniversityData.university_name,
          scholarUrl: newUniversityData.scholar_url,
          yearsBack: yearsBack,
          isTrial: newUniversity.isTrial,
          universityToken: universityToken // Include the token in the request
        })
      });

      if (!response.ok) {
        throw new Error("Failed to trigger extraction");
      }

      const data = await response.json();

      setUniversities(prevUniversities => 
        prevUniversities.map(uni => 
          uni.scholar_url === newUniversityData.scholar_url
            ? { ...uni, status: "Completed", researchers_count: data.researchersCount }
            : uni
        )
      );
    } catch (error) {
      console.error("Error adding university:", error);
      setUniversities(prevUniversities => 
        prevUniversities.map(uni => 
          uni.scholar_url === newUniversityData.scholar_url
            ? { ...uni, status: "Failed" }
            : uni
        )
      );
      setError("Failed to add university");
    }
  };

  // Update the selectUniversityForExtraction function
  const selectUniversityForExtraction = (university) => {
    setSelectedUniversity(university);
    setSearchResults([]);
    setUniversitySearchTerm('');
    
    // Pre-fill the extraction form with university data
    setNewUniversity({
      ...newUniversity,
      name: university.display_name,
      scholarUrl: extractOpenAlexId(university.id),
    });
  };

  // Update the runBigQueryProcess function
  const runBigQueryProcess = async () => {
    setIsProcessing(true);
    setBigQueryStatus('Processing...');
    
    try {
      const accessToken = getAccessToken();
      if (!accessToken) throw new Error("No access token found");

      const response = await fetch('https://dashport-integrations-api.lookermetrics.com/run-bigquery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          institutionId: extractOpenAlexId(selectedUniversity.id),
          universityToken: newUniversity.universityToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to run BigQuery process");
      }

      await response.json();
      setBigQueryStatus('Success! Data is now available in the RedBook 2.0 Prod dashboard.');
    } catch (error) {
      console.error("Error running BigQuery process:", error);
      setBigQueryStatus(`Error: ${error.message}`);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderDataPipeline = () => {
    const truncateUrl = (url, maxLength = 30) => {
      if (url.length <= maxLength) return url;
      return url.substring(0, maxLength - 3) + "...";
    };

    const formatYearsBack = (yearsBack) => {
      if (yearsBack === 0) return "All Time";
      return yearsBack === 1 ? "1 Year" : `${yearsBack} Years`;
    };

    // STEP 1: University Search Section
    const renderUniversitySearch = () => (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Step 1: Find University</h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className='mb-3'>
            <Form.Label>Search OpenAlex for Institution</Form.Label>
            <InputGroup>
              <Form.Control
                type='text'
                placeholder='Type to search for a university...'
                value={universitySearchTerm}
                onChange={handleUniversitySearch}
              />
              <Button 
                variant='outline-secondary'
                onClick={() => searchUniversities()}
                disabled={isSearching || universitySearchTerm.length < 3}
              >
                {isSearching ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className='bi bi-search'></i>
                )}
              </Button>
            </InputGroup>
            
            {/* Show search results */}
            {searchResults.length > 0 && (
              <ListGroup className='mt-2'>
                {searchResults.map(result => (
                  <ListGroup.Item 
                    key={result.id} 
                    action 
                    onClick={() => selectUniversityForExtraction(result)}
                    className='d-flex justify-content-between align-items-center'
                  >
                    <div>{result.display_name}</div>
                    <small className='text-muted'>{result.hint}</small>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            
            {/* Show selected university */}
            {selectedUniversity && (
              <div className='mt-2 p-2 border rounded bg-light'>
                <div className='d-flex justify-content-between align-items-center'>
                  <div>
                    <strong>Selected:</strong> {selectedUniversity.display_name} 
                    {selectedUniversity.hint && <small className='ms-2 text-muted'>({selectedUniversity.hint})</small>}
                  </div>
                  <Button 
                    variant='outline-danger' 
                    size='sm'
                    onClick={() => {
                      setSelectedUniversity(null);
                      setNewUniversity({
                        ...newUniversity,
                        name: "",
                        scholarUrl: "",
                      });
                    }}
                  >
                    <i className='bi bi-x'></i>
                  </Button>
                </div>
                <div className='mt-1'>
                  <small>OpenAlex ID: <code><a href={extractOpenAlexId(selectedUniversity.id)} target="_blank" rel="noopener noreferrer">{extractOpenAlexId(selectedUniversity.id)}</a></code></small>
                </div>
                
                {/* If you want to prefill the form fields */}
                <div className="d-grid gap-2 mt-2">
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={() => {
                      setNewUniversity({
                        ...newUniversity,
                        name: selectedUniversity.display_name,
                        scholarUrl: extractOpenAlexId(selectedUniversity.id),
                        yearsBack: "5",
                        isTrial: false
                      });
                    }}
                  >
                    Use This University
                  </Button>
                </div>
              </div>
            )}
          </Form.Group>
        </Card.Body>
      </Card>
    );

    // STEP 2: Logo Management Section
    const renderLogoManagement = () => {
      if (!selectedUniversity) return null;
      
      return (
        <div className="mb-4">
          <EntityLogoManager selectedEntity={selectedUniversity} entityType="university" />
        </div>
      );
    };

    // STEP 3: Configure Extraction Parameters
    const renderExtractionForm = () => (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Step 2: Configure & Load University Data</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={addUniversity}>
            <div className='d-flex align-items-end mb-3'>
              <Form.Group className='me-2' style={{ width: "35%" }}>
                <Form.Label>University Name</Form.Label>
                <Form.Control
                  type='text'
                  name="name"
                  value={newUniversity.name}
                  onChange={handleUniversityInputChange}
                  required
                />
              </Form.Group>
              <Form.Group className='me-2' style={{ width: "30%" }}>
                <Form.Label>OpenAlex ID</Form.Label>
                <Form.Control
                  type='url'
                  name="scholarUrl"
                  value={newUniversity.scholarUrl}
                  onChange={handleUniversityInputChange}
                  required
                />
              </Form.Group>
              <Form.Group className='me-2' style={{ width: "15%" }}>
                <Form.Label>Timeframe</Form.Label>
                <Form.Select
                  name="yearsBack"
                  value={newUniversity.yearsBack}
                  onChange={handleUniversityInputChange}
                  required
                >
                  <option value="all-time">All Time</option>
                  {[...Array(10)].map((_, i) => (
                    <option key={i+1} value={i+1}>{i+1} {i === 0 ? 'Year' : 'Years'}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='me-2' style={{ width: "10%" }}>
                <Form.Label>Type</Form.Label>
                <Form.Select
                  name="isTrial"
                  value={newUniversity.isTrial}
                  onChange={handleUniversityInputChange}
                >
                  <option value={true}>Trial</option>
                  <option value={false}>Paid</option>
                </Form.Select>
              </Form.Group>
              <div style={{ width: "10%" }}>
                <Button type='submit' style={{ height: "38px", width: "100%" }}>
                  Add
                </Button>
              </div>
            </div>
            
            {/* BigQuery processing section */}
            {newUniversity.universityToken && (
              <div className="mt-3">
                <Alert variant="info">
                  <div className="d-flex justify-content-between">
                    <div>
                      <strong>University Token:</strong> {newUniversity.universityToken}
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={runBigQueryProcess}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-1" />
                          Processing...
                        </>
                      ) : (
                        <>Run BigQuery Process</>
                      )}
                    </Button>
                  </div>
                  {bigQueryStatus && (
                    <div className="mt-2">
                      <strong>Status:</strong> {bigQueryStatus}
                    </div>
                  )}
                </Alert>
              </div>
            )}
          </Form>

          {error && <p className='text-danger mt-3'>{error}</p>}
        </Card.Body>
      </Card>
    );

    // STEP 4: Extraction List
    const renderExtractionsTable = () => (
      <Card>
        <Card.Header>
          <h5 className="mb-0">University Extractions</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table className='mb-0' style={{ fontSize: "0.9rem" }}>
            <thead>
              <tr>
                <th style={{ width: "20%" }}>University Name</th>
                <th style={{ width: "25%" }}>Scholar URL</th>
                <th style={{ width: "15%" }}>Publication Timeframe</th>
                <th style={{ width: "10%" }}>Type</th>
                <th style={{ width: "15%" }}>Status</th>
                <th style={{ width: "10%" }}>Count</th>
                <th style={{ width: "15%" }}>Last Sync</th>
              </tr>
            </thead>
            <tbody>
              {universities.map((uni, index) => (
                <tr key={index}>
                  <td style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {uni.university_name}
                  </td>
                  <td style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    <a href={uni.scholar_url} target='_blank' rel='noopener noreferrer' title={uni.scholar_url}>
                      {truncateUrl(uni.scholar_url)}
                    </a>
                  </td>
                  <td>{formatYearsBack(uni.years_back)}</td>
                  <td>
                    <Badge bg={uni.is_trial ? 'warning' : 'success'}>
                      {uni.is_trial ? 'Trial' : 'Paid'}
                    </Badge>
                  </td>
                  <td>{uni.status}</td>
                  <td>{uni.researchers_count || "-"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {new Date(uni.last_sync_date).toLocaleDateString()} {new Date(uni.last_sync_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                </tr>
              ))}
              {Array.from({ length: Math.max(0, 10 - universities.length) }).map(
                (_, index) => (
                  <tr key={`empty-${index}`}>
                    <td colSpan='7'>&nbsp;</td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );

    return (
      <div>
        {renderUniversitySearch()}
        {renderLogoManagement()}
        {renderExtractionForm()}
        {renderExtractionsTable()}
      </div>
    );
  };

  // Add funder search function
  const searchFunders = useCallback(async (term) => {
    if (!term || term.length < 3) return;
    
    setIsFunderSearching(true);
    try {
      const response = await fetch(`https://api.openalex.org/autocomplete/funders?q=${encodeURIComponent(term)}`);
      
      if (response.status === 429) {
        console.log('Rate limit reached for OpenAlex API');
        return;
      }
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      setFunderSearchResults(data.results || []);
    } catch (error) {
      console.error('Error searching funders:', error);
    } finally {
      setIsFunderSearching(false);
    }
  }, []);

  // Add funder search handler
  const handleFunderSearch = (e) => {
    setFunderSearchTerm(e.target.value);
    
    if (e.target.value.length > 2) {
      debouncedFunderSearch(e.target.value);
    } else {
      setFunderSearchResults([]);
    }
  };

  // Debounce funder search
  const debouncedFunderSearch = useCallback(
    (term) => {
      const debounced = _.debounce((searchTerm) => {
        searchFunders(searchTerm);
      }, 500);
      debounced(term);
      return () => debounced.cancel();
    },
    [searchFunders]
  );

  const selectFunder = (funder) => {
    setSelectedFunder(funder);
    setFunderSearchResults([]);
    setFunderSearchTerm('');
    
    // Construct the full Looker Studio dashboard URL with the funder ID
    const dashboardUrl = constructDashboardUrl(funder.id, 'funder');
    
    setNewUser({
      ...newUser,
      university_token: dashboardUrl,
      university_name: funder.display_name,
      custom_links: newUser.custom_links
    });
  };

  // Add this function near the other extraction functions
  const extractFunderId = (url) => {
    try {
      // If it's already an OpenAlex URL, return it
      if (url.includes('openalex.org/F')) {
        return url;
      }
      
      // If it's a dashboard URL, extract the OpenAlex URL from the params
      const paramsMatch = url.match(/params=(.+)$/);
      if (paramsMatch) {
        const params = JSON.parse(decodeURIComponent(paramsMatch[1]));
        const funderId = params["ds254.funderid"];
        // If we found a funder ID in the params, return it
        if (funderId && funderId.includes('openalex.org/F')) {
          return funderId;
        }
      }
      
      // If no match found or not an OpenAlex URL, try to extract just the ID
      const idMatch = url.match(/openalex\.org\/F(\d+)/);
      if (idMatch) {
        return `https://openalex.org/F${idMatch[1]}`;
      }
      
      // If all else fails, return the original URL
      return url;
    } catch (e) {
      // If there's any error parsing, return the original URL
      return url;
    }
  };

  const handlePasswordReset = async () => {
    const email = passwordResetModal.email;
    setPasswordResetModal({ show: false, email: null }); // Close modal
    
    try {
      setPasswordResetStatus({ email, status: "loading", message: "Sending reset email..." });
      
      const accessToken = getAccessToken();
      if (!accessToken) throw new Error("No access token found");

      const response = await fetch(`${API_URL}/users/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      
      if (!response.ok) throw new Error(data.error || "Failed to send reset email");
      
      setPasswordResetStatus({ 
        email, 
        status: "success", 
        message: "Password reset email sent successfully!" 
      });
      
      // Clear status after 5 seconds
      setTimeout(() => {
        setPasswordResetStatus({ email: null, status: null, message: "" });
      }, 5000);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setPasswordResetStatus({ 
        email, 
        status: "error", 
        message: `Failed to send reset email: ${error.message}` 
      });
    }
  };

  const sendPasswordReset = (email) => {
    setPasswordResetModal({ show: true, email });
  };

  return (
    <Card>
      <Card.Body className='p-0'>
        <Nav variant='tabs' className='border-bottom'>
          <Nav.Item>
            <Nav.Link
              active={activeTab === "users"}
              onClick={() => setActiveTab("users")}
              className={`px-4 py-2 rounded-0 ${
                activeTab === "users" ? "fw-bold" : ""
              }`}
              style={{
                border: "none",
                borderBottom:
                  activeTab === "users" ? "3px solid #0d6efd" : "none",
                backgroundColor: "white",
                color: activeTab === "users" ? "#0d6efd" : "#495057",
                marginBottom: "-1px",
              }}
            >
              User Management
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeTab === "pipeline"}
              onClick={() => setActiveTab("pipeline")}
              className={`px-4 py-2 rounded-0 ${
                activeTab === "pipeline" ? "fw-bold" : ""
              }`}
              style={{
                border: "none",
                borderBottom:
                  activeTab === "pipeline" ? "3px solid #0d6efd" : "none",
                backgroundColor: "white",
                color: activeTab === "pipeline" ? "#0d6efd" : "#495057",
                marginBottom: "-1px",
              }}
            >
              University Management
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <div className='p-3'>
          {activeTab === "users"
            ? renderUserManagement()
            : renderDataPipeline()}

          {error && <p className='text-danger mt-3'>{error}</p>}
        </div>

        <Toast
          ref={toastRef}
          onClose={() => setShowPasswordToast(false)}
          show={showPasswordToast}
          delay={30000}
          autohide
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            minWidth: 300,
            zIndex: 1000,
          }}
        >
          <Toast.Header>
            <strong className='me-auto'>New User's Password</strong>
          </Toast.Header>
          <Toast.Body>
            <p>Password: {generatedPassword}</p>
            <Button
              onClick={copyPasswordToClipboard}
              variant='primary'
              size='sm'
            >
              Copy Password
            </Button>
          </Toast.Body>
        </Toast>

        <Toast
          show={passwordResetStatus.email !== null}
          onClose={() => setPasswordResetStatus({ email: null, status: null, message: "" })}
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            minWidth: 300,
            zIndex: 1000,
          }}
          bg={passwordResetStatus.status === "success" ? "success" : 
              passwordResetStatus.status === "error" ? "danger" : "primary"}
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Password Reset</strong>
          </Toast.Header>
          <Toast.Body className={passwordResetStatus.status === "success" ? "text-white" : ""}>
            {passwordResetStatus.status === "loading" ? (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                {passwordResetStatus.message}
              </div>
            ) : (
              passwordResetStatus.message
            )}
          </Toast.Body>
        </Toast>

        {/* Add the password reset confirmation modal */}
        <Modal
          show={passwordResetModal.show}
          onHide={() => setPasswordResetModal({ show: false, email: null })}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Password Reset</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to send a password reset email to:</p>
            <p className="text-primary fw-bold">{passwordResetModal.email}</p>
            <p className="text-muted mb-0">
              <i className="bi bi-info-circle me-2"></i>
              The user will receive an email with instructions to reset their password.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setPasswordResetModal({ show: false, email: null })}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handlePasswordReset}
            >
              Send Reset Email
            </Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
};
